import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import MemberSearchableDropdown, { MemberOption } from '@/components/ui/MemberSearchableDropdown';
import { Modal, Button } from '@/components/ui';
import LockerKeyPicker from '@/components/lockers/LockerKeyPicker';
import { MemberDirectoryEntry, LockerRoom } from '@/types';
import { Loader2 } from 'lucide-react';
import { useMemberMemberships, useMembershipFreezes, useMemberCheckedInToday, usePermissions, useRefreshMemberDirectoryOnMiss } from '@/hooks';
import { memberMatchesQuery } from '@/lib/member-search';
import { useMemberWebcamPhotos } from '@/hooks/useMemberWebcamPhotos';
import { useMemberUploadedPhotos } from '@/hooks/useMemberUploadedPhotos';
import {
  MEMBER_PHOTO_QUERY_KEYS,
  MEMBERSHIP_QUERY_KEYS,
  CHECK_IN_QUERY_KEYS,
} from '@/constants';
import { MembershipStatusBadge, BirthdayNotification } from '@/components/members';
import { MembershipFrozenNotification } from '@/components/memberships';
import { calculateDaysRemaining, getActiveMemberships, useMembershipsToDisplay } from '@/utils';
import { getCurrentFreeze } from '@/utils/freeze-validation';
import { lockerRoomForGender } from '@/lib/locker-utils';
import { getBestPhotoUrl } from '@/lib';

interface CheckInModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * `keyOnly` asks the host to hand out the locker key against the member's
   * EXISTING check-in rather than creating a second one. Only ever true when
   * the member is already checked in today and a locker is selected.
   */
  onCheckIn: (memberId: string, lockerId?: string, keyOnly?: boolean) => void;
  members?: MemberDirectoryEntry[];
  isLoading?: boolean;
  /** When provided, skips the member dropdown and shows this member directly */
  preSelectedMemberId?: string;
  /** Pre-selected member object (for birthday notification when using preSelectedMemberId) */
  preSelectedMember?: MemberDirectoryEntry;
  /**
   * Render the member's name as a Link to their profile that closes the modal.
   * Off by default: MemberViewPage is already on the profile, and hosts without
   * a Router context can't render a Link.
   */
  linkNameToProfile?: boolean;
}

export default function CheckInModal({
  isOpen,
  onClose,
  onCheckIn,
  members,
  isLoading,
  preSelectedMemberId,
  preSelectedMember,
  linkNameToProfile,
}: CheckInModalProps) {
  const [selectedMemberId, setSelectedMemberId] = useState<string | undefined>(undefined);
  const [selectedLockerId, setSelectedLockerId] = useState<string | null>(null);
  // Which of the two submit buttons is in flight. `isLoading` alone can't say,
  // and both can be visible at once (already checked in + locker picked), so
  // without this the spinner would land on whichever button the markup checks
  // first rather than the one the admin pressed.
  const [pendingAction, setPendingAction] = useState<'check-in' | 'key-only' | null>(null);
  const effectiveMemberId = preSelectedMemberId || selectedMemberId;
  // Dropdown mode is fed the cached member directory; a member who signed up
  // on the app minutes ago isn't in it yet, so a search that finds nobody
  // refetches it once.
  const [memberSearch, setMemberSearch] = useState('');
  const memberSearchMatches = useMemo(
    () =>
      memberSearch.trim().length < 3
        ? 0
        : (members?.filter((member) => memberMatchesQuery(member, memberSearch)).length ?? 0),
    [members, memberSearch],
  );
  useRefreshMemberDirectoryOnMiss(memberSearch, memberSearchMatches);
  const queryClient = useQueryClient();
  const { hasAccessTo } = usePermissions();

  // Locker choice is per member and per open: drop it when the member changes
  // (dropdown mode) or the modal closes/reopens.
  useEffect(() => {
    setSelectedLockerId(null);
  }, [effectiveMemberId, isOpen]);

  // Dropdown-mode member selection resets on close (success closes the modal
  // via the host's onSuccess), NOT on submit — an async failure (e.g. check-in
  // cooldown) keeps the modal open, and clearing on submit would blank the
  // member card and wipe the locker choice before the outcome is known.
  useEffect(() => {
    if (!isOpen) setSelectedMemberId(undefined);
  }, [isOpen]);

  // Fetch memberships for selected member. Use `isFetching` so we also show the
  // skeleton during background refetches triggered by an upstream invalidation
  // (e.g. just-added subscription) — `isLoading` only covers the very first load.
  const {
    data: memberships,
    isLoading: isLoadingMemberships,
    isFetching: isFetchingMemberships,
  } = useMemberMemberships(effectiveMemberId || '');

  // Fetch photos for selected member
  const {
    data: webcamPhotos,
    isLoading: isLoadingWebcamPhoto,
    isFetching: isFetchingWebcamPhoto,
  } = useMemberWebcamPhotos(effectiveMemberId || '');
  const {
    data: uploadedPhotos,
    isLoading: isLoadingUploadedPhoto,
    isFetching: isFetchingUploadedPhoto,
  } = useMemberUploadedPhotos(effectiveMemberId || '');

  // When the modal opens with a pre-selected member, force a refetch so we
  // never paint stale cache from before the parent's mutations settled.
  useEffect(() => {
    if (!isOpen || !effectiveMemberId) return;
    queryClient.refetchQueries({
      queryKey: MEMBERSHIP_QUERY_KEYS.BY_MEMBER(effectiveMemberId),
    });
    queryClient.refetchQueries({
      queryKey: MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(effectiveMemberId),
    });
    queryClient.refetchQueries({
      queryKey: MEMBER_PHOTO_QUERY_KEYS.UPLOADED_BY_MEMBER(effectiveMemberId),
    });
    queryClient.refetchQueries({
      queryKey: [...CHECK_IN_QUERY_KEYS.TODAY, 'member', effectiveMemberId],
    });
  }, [isOpen, effectiveMemberId, queryClient]);

  const latestWebcamPhoto = webcamPhotos?.[0] || null;
  const latestUploadedPhoto = uploadedPhotos?.[0] || null;

  // Get selected member for birthday notification
  const selectedMember = preSelectedMember || members?.find(m => m.id === effectiveMemberId);

  // The locker section only appears when the member's gender names a locker
  // room — 'prefer_not_to_say' / null / anything else means we can't know which
  // room to offer, so those members get the plain check-in flow. It is also
  // gated by LOCKERS access (same section that shows the Lockers page), so an
  // admin who can't manage lockers never sees a key control RLS would reject.
  const lockerRoom: LockerRoom | null = hasAccessTo('LOCKERS')
    ? lockerRoomForGender(selectedMember?.gender)
    : null;
  const showLockerSection = Boolean(effectiveMemberId && lockerRoom);

  // Unified display memberships (active + upcoming, or single fallback if neither).
  const { memberships: displayMemberships } = useMembershipsToDisplay(memberships);

  // Freeze check still keys on the currently-active membership only — upcoming/expired
  // memberships can't be frozen.
  const primaryActiveMembership = getActiveMemberships(memberships || [])[0] || null;
  const { data: freezes } = useMembershipFreezes(primaryActiveMembership?.id || '');
  const currentFreeze = getCurrentFreeze(freezes);
  const isFrozen = Boolean(currentFreeze);

  const { data: todayCheckInCount } = useMemberCheckedInToday(effectiveMemberId);
  const alreadyCheckedInToday = (todayCheckInCount || 0) > 0;

  const memberOptions: MemberOption[] = members?.map(member => ({
    value: member.id,
    label: `${member.first_name} ${member.last_name} (${member.email})`,
    member: member
  })) || [];

  const handleMemberChange = (memberId: string | null) => {
    setSelectedMemberId(memberId || undefined);
  };

  const handleSubmit = (keyOnly = false) => {
    if (effectiveMemberId) {
      // No state resets here: the submit outcome is async. On success the host
      // closes the modal and the !isOpen effects clear both selections; on a
      // cooldown/error failure the modal stays open with member and locker
      // intact for a retry.
      setPendingAction(keyOnly ? 'key-only' : 'check-in');
      onCheckIn(effectiveMemberId, selectedLockerId ?? undefined, keyOnly);
    }
  };

  // The host's isLoading is the only signal that the submit settled (success
  // closes the modal, failure just drops isLoading), so clear the pending
  // marker off that rather than guessing from the outcome.
  useEffect(() => {
    if (!isLoading) setPendingAction(null);
  }, [isLoading]);

  /**
   * The member is already in the gym and the admin has picked a locker — so the
   * intent is a key handout, not a second check-in. Offering this as the primary
   * action is what stops reception re-checking someone in just to reach the
   * locker grid (the Iwan Izzard duplicate, 14 Sep 2026).
   */
  const canGiveKeyOnly = alreadyCheckedInToday && Boolean(selectedLockerId);

  const webcamUrl = latestWebcamPhoto
    ? getBestPhotoUrl(latestWebcamPhoto.large_photo_url, latestWebcamPhoto.thumbnail_photo_url, latestWebcamPhoto.original_photo_url)
    : null;
  const profileUrl = latestUploadedPhoto
    ? getBestPhotoUrl(latestUploadedPhoto.large_photo_url, latestUploadedPhoto.thumbnail_photo_url, latestUploadedPhoto.original_photo_url)
    : null;

  const PhotoPlaceholder = ({ size = 'lg' }: { size?: 'lg' | 'sm' }) => (
    <svg className={`${size === 'lg' ? 'h-16 w-16' : 'h-8 w-8'} text-gray-300`} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Check-In Member"
      className={showLockerSection ? 'max-w-2xl' : 'max-w-md'}
    >
      {/* Member search (dropdown mode only) */}
      {!preSelectedMemberId && (
        <div className="mb-5">
          <MemberSearchableDropdown
            options={memberOptions}
            value={selectedMemberId}
            onChange={handleMemberChange}
            onInputChange={setMemberSearch}
            placeholder="Search member..."
            isLoading={isLoading}
          />
        </div>
      )}

      {effectiveMemberId && (
        <div className="flex flex-col items-center">
          {/* Photo hero */}
          <div className="relative mb-4">
            {/* Main photo (webcam) */}
            <div className="w-40 h-40 rounded-full overflow-hidden bg-gray-50 border-4 border-white shadow-lg flex items-center justify-center">
              {isFetchingWebcamPhoto || isLoadingWebcamPhoto ? (
                <div className="w-full h-full bg-gray-100 animate-pulse rounded-full" />
              ) : webcamUrl ? (
                <img src={webcamUrl} alt="Webcam photo" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <PhotoPlaceholder size="lg" />
              )}
            </div>
            {/* Profile photo (small overlay) */}
            <div className="absolute -bottom-1 -right-1 w-20 h-20 rounded-full overflow-hidden bg-gray-50 border-3 border-white shadow-md flex items-center justify-center ring-2 ring-white">
              {isFetchingUploadedPhoto || isLoadingUploadedPhoto ? (
                <div className="w-full h-full bg-gray-100 animate-pulse rounded-full" />
              ) : profileUrl ? (
                <img src={profileUrl} alt="Profile photo" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <PhotoPlaceholder size="sm" />
              )}
            </div>
          </div>

          {/* Member name */}
          {linkNameToProfile && selectedMember ? (
            <Link
              to={`/members/${selectedMember.id}`}
              onClick={onClose}
              className="text-xl font-semibold text-gray-900 hover:underline"
            >
              {selectedMember.first_name} {selectedMember.last_name}
            </Link>
          ) : (
            <h3 className="text-xl font-semibold text-gray-900">
              {selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : 'Loading...'}
            </h3>
          )}

          {/* Membership info */}
          <div className="mt-2 flex flex-col items-center gap-1">
            {isFetchingMemberships || isLoadingMemberships ? (
              <MembershipStatusBadge membership={null} isLoading={true} />
            ) : displayMemberships.length > 0 ? (
              displayMemberships.map((membership) => {
                // `calculateDaysRemaining` returns null for upcoming/expired, so the
                // "N days left" line only shows under active memberships.
                const daysRemaining = calculateDaysRemaining(membership);
                return (
                  <div key={membership.id} className="flex items-center gap-2">
                    <MembershipStatusBadge membership={membership} isLoading={false} />
                    {daysRemaining !== null && (
                      <span className="text-sm text-gray-500">
                        {daysRemaining === 1 ? '1 day left' : `${daysRemaining} days left`}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <MembershipStatusBadge membership={null} isLoading={false} />
            )}
          </div>

          {/* Notifications */}
          <div className="mt-4 w-full space-y-2">
            {alreadyCheckedInToday && (
              <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
                <span className="font-semibold">Already checked in today</span>
                {todayCheckInCount && todayCheckInCount > 1 ? ` (${todayCheckInCount} times)` : ''}.{' '}
                {lockerRoom
                  ? 'Only need a locker key? Pick one below and use “Give key only” — that avoids a duplicate check-in.'
                  : 'Only check in again if they have left and come back.'}
              </div>
            )}
            <MembershipFrozenNotification firstName={selectedMember?.first_name || ''} freeze={currentFreeze} />
            <BirthdayNotification firstName={selectedMember?.first_name || ''} dateOfBirth={selectedMember?.date_of_birth} />
          </div>

          {/* Optional locker handout — only when gender names a room. Conditional
              mounting is what gates the locker queries to visible use. */}
          {effectiveMemberId && lockerRoom && (
            <div className="mt-5 w-full">
              <LockerKeyPicker
                memberId={effectiveMemberId}
                room={lockerRoom}
                selectedLockerId={selectedLockerId}
                onSelect={setSelectedLockerId}
                helpText={
                  alreadyCheckedInToday
                    ? 'Tap a free locker, then “Give key only” to hand out the key without a second check-in.'
                    : 'Tap a free locker to give out its key with this check-in. Tap again to deselect.'
                }
                errorText={
                  'Couldn’t load lockers. You can still check the member in — hand out a key from the Lockers page afterwards.'
                }
              />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-3 mt-6">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {/* Key-only leads once it applies; checking in again stays one click
            away for the member who genuinely left and came back. */}
        <Button
          variant={canGiveKeyOnly ? 'outline' : 'default'}
          onClick={() => handleSubmit(false)}
          disabled={!effectiveMemberId || isFrozen || isLoading}
          data-testid="check-in-submit"
        >
          {isLoading && pendingAction !== 'key-only' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {alreadyCheckedInToday ? 'Check in again' : 'Check In'}
        </Button>
        {canGiveKeyOnly && (
          <Button
            onClick={() => handleSubmit(true)}
            disabled={isFrozen || isLoading}
            data-testid="give-key-only"
          >
            {isLoading && pendingAction === 'key-only' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Give key only
          </Button>
        )}
      </div>
    </Modal>
  );
}